/* The cascading navigation drawer (nav-megamenu, navigation_style == 'panels'). Extends
 * Prestige's Drawer (x-drawer -> Modal -> DialogElement in theme.js) for the overlay, scroll lock,
 * focus trap and editor lifecycle, and adds the pane cascade and its motion on top.
 * Collection previews: a series row ships as a link marked data-vrp-preview="<collection handle>";
 * on the first open it becomes a pane-3 button with an empty pane, and the prints are fetched from
 * sections/vrp-nav-prints-preview.liquid on hover, focus or choice. A row marked
 * data-vrp-preview-kind="filtered" carries a filtered collection address instead and stays a link:
 * its prints show while it is hovered or focused, turning one at a time or as cards, and the link
 * itself opens the results; a touch opens the prints first and follows the link once they are showing. */
customElements.whenDefined('x-drawer').then(() => {
  if (customElements.get('vrp-nav-drawer')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESKTOP = window.matchMedia(window.themeVariables.mediaQueries.md);
  const FINE_POINTER = window.matchMedia(window.themeVariables.mediaQueries['supports-hover']);
  const TRIGGER = '[data-vrp-nav-open]';
  const ROW = '[data-vrp-row]';
  const BACK = '[data-vrp-back]';
  const CLOSE = '[data-vrp-close]';
  const COVERED = 'data-vrp-covered';
  const ACTIVE_CLASS = 'vrp-nav-active';
  const PREVIEW = '[data-vrp-preview]';
  const PREVIEW_BODY = '[data-vrp-preview-body]';
  const PREVIEW_LINK = '[data-vrp-preview-link]';
  const PREVIEW_LIST = '[data-vrp-preview-list]';
  const PREVIEW_STAGE = '[data-vrp-preview-stage]';
  const STAGE = '[data-vrp-stage]';
  const FILTERED = 'filtered';
  // The section file fetched for a row's prints; renaming the file breaks this string.
  const PREVIEW_SECTION = 'vrp-nav-prints-preview';
  // How long the pointer rests on a series row before its pane opens; a pass over the list opens nothing.
  const HOVER_INTENT = 120;
  // One fetch per collection handle or address for the life of the page; the count to show is read on insert.
  const PREVIEW_CACHE = new Map();
  // A rotating preview's prints, kept beside its stage until their turn: nothing off the stage is requested.
  const STAGES = new WeakMap();
  // How long one print fades in over the last (vrp-nav-drawer.css); the last leaves once that has run.
  const STAGE_FADE = 400;
  // One 0.7s ease slide for the whole drawer; panes and phone levels move faster.
  const DRAWER_SLIDE = { duration: 700, easing: 'ease' };
  const PANE_SLIDE = { duration: 450, easing: 'ease' };
  const LEVEL_SLIDE = { duration: 300, easing: 'ease' };

  class VrpNavDrawer extends customElements.get('x-drawer') {
    #l1 = null;
    #l2 = null;
    #l3 = null;
    // Panes on screen after the last render, and which way the phone last moved between levels.
    #shown = [];
    #direction = 1;
    // True from the start of the closing slide to its end; a trigger click in between is dropped,
    // because the base class would finish the old slide by hiding a drawer that has just reopened.
    #leaving = false;
    #hoverTimer = null;
    // Set while this class moves focus itself, so a focus the visitor did not make opens no preview.
    #quietFocus = false;
    #previewRequest = 0;
    #pendingPreview = null;
    // The rotating preview whose prints are turning, if any: its stage, pane, the rows that open it, and the timer.
    #rotation = null;

    constructor() {
      super();
      this.addEventListener('click', this.#onClick);
      this.addEventListener('pointerover', this.#onPointerOver);
      this.addEventListener('pointerout', this.#onPointerOut);
      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('focusout', (event) => {
        if (this.#pendingPreview && !this.contains(event.relatedTarget)) this.#cancelPreview();
        if (this.#rotation && !this.#rotationHolds(event.relatedTarget) && !this.#rotation.pane.matches(':hover')) this.#stopRotation();
      });
      this.addEventListener('dialog:before-show', () => {
        document.documentElement.classList.add(ACTIVE_CLASS);
        const header = this.#header();
        if (header) this.style.setProperty('--vrp-header-bottom', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
        this.#upgradePreviews();
        this.#shown = [];
        this.#render();
      });
      this.addEventListener('dialog:before-hide', () => {
        this.#leaving = true;
        this.#cancelPreview();
      });
      this.addEventListener('dialog:after-show', () => {
        if (FINE_POINTER.matches) this.#focusFirstRow(this.#entryPane());
      });
      this.addEventListener('dialog:after-hide', () => {
        this.#leaving = false;
        // Reopened by script while the closing slide ran (the editor moving between two blocks does
        // this): the base class has just hidden a drawer that is open, so put it back on screen.
        if (this.open) {
          this.style.setProperty('display', 'block');
          this.#shown = [];
          this.#render();
          return;
        }
        this.#release();
        this.#reset();
      });
    }

    // DialogElement moves the element to <body> on open and back on close, so this runs on
    // every open/close; only AbortController-scoped listeners belong here (HeaderSidebar does the same).
    connectedCallback() {
      super.connectedCallback();
      const { signal } = this.abortController;
      document.addEventListener('click', this.#onTriggerClick, { capture: true, signal });
      DESKTOP.addEventListener('change', () => {
        this.#cancelPreview();
        this.#syncFilteredRows();
        if (this.open) this.#render();
      }, { signal });
      FINE_POINTER.addEventListener('change', () => this.#syncFilteredRows(), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.#stopRotation();
        else if (this.open) this.#startRotation(this.#paneByKey(this.#l3));
      }, { signal });
      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', this.#onBlockSelect, { capture: true, signal });
      }
    }

    // The base class moves the element to <body> and back, which also lands here; only a drawer that
    // is really gone (a section re-render in the editor, say) gives the header back.
    disconnectedCallback() {
      super.disconnectedCallback();
      queueMicrotask(() => {
        if (!this.isConnected) {
          this.#cancelPreview();
          this.#release();
        }
      });
    }

    // Desktop: the whole drawer slides as one piece over 0.7s. A phone keeps Prestige's own 0.3s drawer slide.
    createEnterAnimationControls() {
      return this.#settle(DESKTOP.matches ? this.#slideDrawer(true) : super.createEnterAnimationControls());
    }

    createLeaveAnimationControls() {
      return this.#settle(DESKTOP.matches ? this.#slideDrawer(false) : super.createLeaveAnimationControls());
    }

    // Same contract as the Motion controls the base class returns: a `finished` promise and `finish()`.
    #slideDrawer(entering) {
      const content = this.getShadowPartByName('content');
      const off = 'translateX(calc(var(--transform-logical-flip) * -100%))';
      content.style.marginInlineStart = 0;
      const animations = [
        this.getShadowPartByName('overlay').animate({ opacity: entering ? [0, 1] : [1, 0] }, DRAWER_SLIDE),
        content.animate({ transform: entering ? [off, 'translateX(0)'] : ['translateX(0)', off] }, DRAWER_SLIDE),
      ];
      return {
        finished: Promise.all(animations.map((animation) => animation.finished)),
        finish: () => animations.forEach((animation) => animation.finish()),
      };
    }

    // A second bar trigger switches groups instead of closing the drawer; the hamburger still toggles it closed.
    hideForOutsideClickTarget(target) {
      const group = this.#groupFor(target?.closest?.(TRIGGER)?.dataset.vrpNavOpen);
      if (group !== null && group !== this.#l1) return false;
      return super.hideForOutsideClickTarget(target);
    }

    #settle(controls) {
      if (REDUCED_MOTION.matches) controls.finish();
      return controls;
    }

    #onTriggerClick = (event) => {
      const trigger = event.target.closest?.(`${TRIGGER}[aria-controls="${this.id}"]`);
      if (!trigger) return;
      if (this.#leaving) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const l1 = this.#groupFor(trigger.dataset.vrpNavOpen);
      if (!this.open) {
        this.#l1 = l1;
        return;
      }
      if (l1 === null || l1 === this.#l1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#cancelPreview();
      this.#l1 = l1;
      this.#l2 = this.#l3 = null;
      // Re-arm the focus trap on the new trigger so Escape returns focus to the group that is showing.
      this.focusTrap.deactivate({ returnFocus: false, onDeactivate: () => {} });
      trigger.focus({ preventScroll: true });
      this.focusTrap.activate();
      this.#render();
      this.#focusFirstRow(this.#entryPane());
    };

    #onClick = (event) => {
      if (event.target.closest(CLOSE)) {
        this.hide();
        return;
      }
      if (event.target.closest(BACK)) {
        event.preventDefault();
        this.#back();
        return;
      }
      const row = event.target.closest(ROW);
      const key = row?.dataset.vrpTarget;
      if (!key) return;
      // A tab row (the phone's way to another tab) changes group, whichever pane it sits in.
      const level = row.hasAttribute('data-vrp-tab') ? 0 : Number(row.closest('[data-vrp-pane]')?.dataset.vrpPane);
      if (level === 2 && !DESKTOP.matches) {
        // Two levels on a phone: a level-3 row acts as the link to its own item.
        const href = row.href || row.dataset.vrpHref;
        if (href) {
          if (!row.href) window.location.assign(href);
          return;
        }
      }
      // A filtered row is a link with a preview: a fine pointer saw the prints on hover and follows the
      // link; a touch opens the prints first and follows the link once they are showing.
      if (row.dataset.vrpPreviewKind === FILTERED && (FINE_POINTER.matches || this.#previewActive(this.#paneByKey(key)))) {
        clearTimeout(this.#hoverTimer);
        return;
      }
      event.preventDefault();
      // Opted-in collection rows also preview on phones; the existing pane-2 series links above do not.
      if (row.dataset.vrpPreview) {
        this.#openPreview(row, true, 'choice');
        return;
      }
      this.#select(row, key, level);
    };

    // Resting the pointer on a series row opens its prints. Nothing is fetched before the rest, so a
    // sweep down the list requests only the row the pointer stops on. A touch, or a phone-width
    // window, leaves the row to its click.
    #onPointerOver = (event) => {
      if (event.pointerType === 'touch' || !DESKTOP.matches) return;
      const row = this.#previewRow(event.target);
      if (this.#pendingPreview?.intent === 'pointer' && this.#pendingPreview.row !== row) this.#cancelPreview();
      if (!row) {
        // The pointer resting on a showing preview keeps its prints turning.
        const pane = event.target.closest?.('[data-vrp-pane]');
        if (this.#previewActive(pane)) this.#startRotation(pane);
        return;
      }
      clearTimeout(this.#hoverTimer);
      const pane = this.#paneByKey(row.dataset.vrpTarget);
      if (this.#previewActive(pane)) {
        this.#startRotation(pane);
        return;
      }
      this.#hoverTimer = setTimeout(() => {
        if (this.open && !this.#leaving && row.matches(':hover')) this.#openPreview(row, false, 'pointer');
      }, HOVER_INTENT);
    };

    #onPointerOut = (event) => {
      const row = this.#previewRow(event.target);
      if (row && !row.contains(event.relatedTarget)) {
        clearTimeout(this.#hoverTimer);
        if (this.#pendingPreview?.row === row && this.#pendingPreview.intent === 'pointer') this.#cancelPreview();
      }
      if (this.#rotation && !this.#rotationHolds(event.relatedTarget)) this.#stopRotation();
    };

    // Keyboard focus landing on a series row shows its prints without moving focus off the row.
    #onFocusIn = (event) => {
      if (this.#quietFocus) return;
      const row = this.#previewRow(event.target);
      if (this.#pendingPreview && this.#pendingPreview.row !== row) this.#cancelPreview();
      if (!DESKTOP.matches) return;
      const pane = row ? this.#paneByKey(row.dataset.vrpTarget) : event.target.closest?.('[data-vrp-pane]');
      if (this.#previewActive(pane)) {
        if (event.target.matches?.(STAGE)) this.#stopRotation();
        else this.#startRotation(pane);
        return;
      }
      if (row) this.#openPreview(row, false, 'focus');
    };

    #onBlockSelect = (event) => {
      const target = event.target.closest?.('[data-vrp-key], [data-vrp-group]');
      if (!target) return;
      this.#cancelPreview();
      const key = target.dataset.vrpKey;
      if (key) {
        const [l1, l2, l3] = key.split('-');
        this.#l1 = l1;
        this.#l2 = l2 && l2 !== 'media' ? `${l1}-${l2}` : null;
        this.#l3 = l3 ? key : null;
      } else {
        this.#l1 = target.closest('[data-vrp-pane="1"]')?.dataset.vrpFor ?? null;
        this.#l2 = this.#l3 = null;
      }
      if (this.open) this.#render();
    };

    #select(row, key, level) {
      this.#cancelPreview();
      this.#direction = 1;
      if (level === 0) {
        this.#l1 = key;
        this.#l2 = this.#l3 = null;
      } else if (level === 1) {
        this.#l2 = this.#l2 === key ? null : key;
        this.#l3 = null;
      } else if (level === 2) {
        this.#l3 = this.#l3 === key ? null : key;
      } else {
        return;
      }
      this.#render();
      const opened = level === 0 ? this.#pane1() : this.#paneByKey(level === 1 ? this.#l2 : this.#l3);
      this.#focusFirstRow(opened);
    }

    #back() {
      this.#cancelPreview();
      this.#direction = -1;
      let reopen;
      if (this.#l3) {
        reopen = this.#paneByKey(this.#l2)?.querySelector(`${ROW}[data-vrp-target="${this.#l3}"]`);
        this.#l3 = null;
      } else if (this.#l2) {
        reopen = this.#pane1()?.querySelector(`${ROW}[data-vrp-target="${this.#l2}"]`);
        this.#l2 = null;
      } else if (this.#l1 !== this.#home()) {
        // From another tab's list back to the first screen, onto the row that led there.
        const from = this.#l1;
        this.#l1 = this.#home();
        reopen = this.#pane1()?.querySelector(`${ROW}[data-vrp-target="${from}"]`);
      } else {
        return;
      }
      this.#render();
      this.#focusQuietly(reopen);
    }

    #render() {
      const panes = this.querySelectorAll('[data-vrp-pane]');
      // With no group asked for (the phone's menu icon) the drawer lands on the first tab's list.
      if (this.#l1 === null) this.#l1 = this.#home();
      // Grouped phone rows live in the home pane but keep their original menu keys. On resize,
      // put an open child tray beside its own desktop group, or back over the phone's home pane.
      if (this.querySelector('.vrp-nav-drawer__mobile-group')) {
        if (!DESKTOP.matches) this.#l1 = this.#home();
        else if (this.#l2) this.#l1 = this.#l2.split('-')[0];
      }
      const pane1 = this.#pane1();
      const list = this.#paneByKey(this.#l2);
      const feature = this.#paneByKey(this.#l3);
      // A phone shows one pane at a time and never the level-1 feature pane.
      const shown = DESKTOP.matches
        ? [pane1, list ?? this.#paneByKey(`${this.#l1}-media`), feature]
        : [feature ?? list ?? pane1];
      if (this.#rotation && !shown.includes(this.#rotation.pane)) this.#stopRotation();
      for (const pane of panes) {
        pane.hidden = !shown.includes(pane);
        if (pane !== pane1 && pane !== list) this.#markRows(pane, null);
        this.#warmPaneMedia(pane, !pane.hidden && this.open);
      }
      this.#movePanes(shown.filter(Boolean));
      for (const part of ['body', 'panel-list']) this.getShadowPartByName(part)?.scrollTo(0, 0);
      this.#markRows(pane1, this.#l2);
      this.#markRows(list, this.#l3);
      this.#syncTriggers();
    }

    #warmPaneMedia(pane, visible) {
      for (const image of pane.querySelectorAll('.vrp-nav-drawer__media img')) {
        const hiddenOnDevice = (!DESKTOP.matches && this.dataset.mobileMedia === 'false')
          || (DESKTOP.matches && image.classList.contains('vrp-nav-drawer__media-image--phone'))
          || (!DESKTOP.matches && image.classList.contains('vrp-nav-drawer__media-image--desktop'));
        image.loading = visible && !hiddenOnDevice ? 'eager' : 'lazy';
      }
    }

    // A pane that was not on screen a moment ago arrives with movement: on desktop it slides out from
    // under the pane to its left (the stylesheet stacks the panes left over right); on a phone the new
    // level comes in from the side the visitor is moving towards. The first render of an opening
    // drawer is left alone: the whole drawer is already sliding.
    #movePanes(shown) {
      const first = !this.#shown.length;
      const fresh = first ? [] : shown.filter((pane) => !this.#shown.includes(pane));
      // Only a pane that was not on screen starts at its top: a list the pointer or the keyboard is
      // moving through keeps its scroll position when a row opens its preview.
      for (const pane of first ? shown : fresh) pane.scrollTop = 0;
      this.#shown = shown;
      if (DESKTOP.matches) {
        this.#coverHeaderParts(shown.reduce((width, pane) => width + pane.offsetWidth, 0));
      }
      if (!this.open || REDUCED_MOTION.matches) return;
      for (const pane of fresh) {
        if (DESKTOP.matches) {
          if (pane.dataset.vrpPane === '1') continue;
          pane.animate({ transform: ['translateX(calc(var(--transform-logical-flip) * -100%))', 'translateX(0)'] }, PANE_SLIDE);
        } else {
          pane.animate({ transform: [`translateX(calc(var(--transform-logical-flip) * ${this.#direction * 100}%))`, 'translateX(0)'] }, LEVEL_SLIDE);
        }
      }
    }

    // Hide the header parts the panes now reach (the logo; the icons on a narrow window).
    #coverHeaderParts(width) {
      const header = this.#header();
      if (!header) return;
      for (const part of header.children) {
        if (part === this || this.controls.some((control) => part.contains(control))) continue;
        const box = part.getBoundingClientRect();
        part.toggleAttribute(COVERED, width > 0 && box.width > 0 && box.left < width);
      }
      // In the nav itself only the open tab's label stays over the drawer; the
      // other labels come back when it closes.
      const triggers = this.controls.filter((control) => control.matches(TRIGGER) && this.#groupFor(control.dataset.vrpNavOpen) !== null);
      const open = triggers.find((control) => control.dataset.vrpNavOpen === this.#l1)?.closest('li');
      for (const item of open?.parentElement.children ?? []) {
        item.toggleAttribute(COVERED, width > 0 && item !== open);
      }
    }

    // Give the header back: no root class, nothing covered.
    #release() {
      document.documentElement.classList.remove(ACTIVE_CLASS);
      for (const part of document.querySelectorAll(`[${COVERED}]`)) part.removeAttribute(COVERED);
    }

    // The header is found from its own triggers, not by name.
    #header() {
      return this.controls.find((control) => control.matches(TRIGGER))?.closest('x-header') ?? null;
    }

    #markRows(pane, selectedKey) {
      if (!pane) return;
      for (const row of pane.querySelectorAll(ROW)) {
        const selected = !!selectedKey && row.dataset.vrpTarget === selectedKey;
        if (selectedKey) row.dataset.vrpState = selected ? 'selected' : 'muted';
        else delete row.dataset.vrpState;
        if (row.hasAttribute('aria-controls')) row.setAttribute('aria-expanded', String(selected));
      }
    }

    #syncTriggers() {
      for (const control of this.controls) {
        if (!control.matches(TRIGGER)) continue;
        const group = this.#groupFor(control.dataset.vrpNavOpen);
        // The menu icon (no group) is the phone's trigger; a bar trigger is expanded only for its own group.
        control.setAttribute('aria-expanded', String(this.open && (group === null ? !DESKTOP.matches : group === this.#l1)));
      }
    }

    #reset() {
      this.#cancelPreview();
      this.#l1 = this.#l2 = this.#l3 = null;
      this.#shown = [];
      for (const pane of this.querySelectorAll('[data-vrp-pane]')) {
        pane.hidden = true;
        this.#warmPaneMedia(pane, false);
        this.#markRows(pane, null);
      }
    }

    // Focus never scrolls a freshly shown pane: on a phone the back row (first in DOM order, at the top,
    // above any media) takes focus so nothing below the image is pulled into view. The first screen
    // has no back row showing, so the first control with a box is taken.
    #focusFirstRow(pane) {
      const controls = pane?.querySelectorAll(DESKTOP.matches ? ROW : `${BACK}, ${ROW}`) ?? [];
      this.#focusQuietly(Array.from(controls).find((control) => control.offsetParent !== null));
    }

    #focusQuietly(element) {
      if (!element) return;
      this.#quietFocus = true;
      element.focus({ preventScroll: true });
      this.#quietFocus = false;
    }

    // A collection row, once upgraded (a button with a pane behind it).
    #previewRow(target) {
      const row = target.closest?.(ROW);
      return row?.dataset.vrpPreview && row.dataset.vrpTarget ? row : null;
    }

    // Series rows ship as plain links so the header HTML carries nothing per collection. On the
    // first open each becomes the same button a Feature media row is, with an empty pane after its
    // list; the pane's key is the row's 1-based place among the list's rows, which is how the
    // snippet numbers level-3 keys. A filtered row stays the link it is and only gains its pane.
    // Runs on every open and finds nothing to do after the first.
    #upgradePreviews() {
      const chevron = this.querySelector(`${ROW}[data-vrp-target] > svg`);
      for (const link of this.querySelectorAll(`a${PREVIEW}:not([data-vrp-target])`)) {
        const list = link.closest('[data-vrp-pane]');
        if (!list) continue;
        const depth = Number(list.dataset.vrpPane) + 1;
        const key = link.dataset.vrpPreviewKey ?? `${list.dataset.vrpKey}-${Array.from(list.querySelectorAll(ROW)).indexOf(link) + 1}`;
        const title = link.textContent.trim();
        let row = link;
        if (link.dataset.vrpPreviewKind !== FILTERED) {
          row = document.createElement('button');
          row.type = 'button';
          row.className = link.className;
          row.dataset.vrpRow = '';
          row.dataset.vrpHref = link.getAttribute('href');
          row.dataset.vrpPreview = link.dataset.vrpPreview;
          if (link.hasAttribute('aria-current')) row.setAttribute('aria-current', link.getAttribute('aria-current'));
          row.textContent = title;
          if (chevron) row.append(chevron.cloneNode(true));
        }
        row.dataset.vrpTarget = key;
        if (row !== link) {
          row.setAttribute('aria-controls', `vrp-nav-pane-${key}`);
          row.setAttribute('aria-expanded', 'false');
          link.replaceWith(row);
        }
        // A phone group and its desktop list share a target pane, but each needs its own button.
        if (this.#paneByKey(key)) continue;
        // After the list and any pane-3 already behind it, so the panes sit in row order as the snippet's do.
        let anchor = list;
        while (Number(anchor.nextElementSibling?.dataset.vrpPane) >= depth) anchor = anchor.nextElementSibling;
        anchor.after(this.#buildPreviewPane(key, list, row, title, depth));
      }
      this.#syncFilteredRows();
    }

    #buildPreviewPane(key, list, row, title, depth) {
      const kind = row.dataset.vrpPreviewKind;
      const rotating = kind === FILTERED && this.dataset.filteredPreviewStyle !== 'cards';
      const pane = document.createElement('div');
      pane.id = `vrp-nav-pane-${key}`;
      pane.className = `vrp-nav-drawer__pane vrp-nav-drawer__pane--feature vrp-nav-drawer__pane--preview${rotating ? ' vrp-nav-drawer__pane--rotating' : ''}`;
      pane.dataset.vrpPane = String(depth);
      pane.dataset.vrpKey = key;
      pane.dataset.vrpPreview = row.dataset.vrpPreview;
      if (kind) pane.dataset.vrpPreviewKind = kind;
      pane.hidden = true;
      // The list's own back row, retitled: on desktop it stays hidden; a window shrunk to phone width
      // while this pane shows still has a way back.
      const back = list.querySelector(BACK)?.cloneNode(true);
      if (back) {
        back.dataset.vrpBack = list.dataset.vrpKey ?? list.dataset.vrpFor;
        const label = Array.from(back.childNodes).findLast((node) => node.nodeType === Node.TEXT_NODE);
        if (label) label.data = title;
        else back.append(title);
        pane.append(back);
      }
      const body = document.createElement('div');
      body.className = 'vrp-nav-drawer__preview';
      body.dataset.vrpPreviewBody = '';
      const link = document.createElement('a');
      link.href = this.#hrefOf(row);
      link.className = 'vrp-nav-drawer__media-link';
      link.dataset.vrpPreviewLink = '';
      link.textContent = this.dataset.previewLinkText ?? '';
      if (rotating) {
        // Laid out like a Feature media row: the picture, then its caption and link under it.
        const media = document.createElement('div');
        media.className = 'vrp-nav-drawer__media vrp-nav-drawer__media--feature';
        const text = document.createElement('p');
        text.className = 'vrp-nav-drawer__media-text';
        const caption = document.createElement('span');
        caption.className = 'vrp-nav-drawer__caption';
        caption.textContent = title;
        text.append(caption, link);
        body.append(media, text);
      } else {
        const heading = document.createElement('p');
        heading.className = 'vrp-nav-drawer__caption vrp-nav-drawer__preview-heading';
        heading.textContent = title;
        body.append(heading, link);
      }
      pane.append(body);
      return pane;
    }

    // A filtered row is a link; it carries expansion semantics only where activating it expands something,
    // which is a touch at desktop width: a fine pointer's click and a phone's tap both follow the link.
    #syncFilteredRows() {
      for (const row of this.querySelectorAll(`${ROW}[data-vrp-preview-kind="${FILTERED}"][data-vrp-target]`)) {
        if (FINE_POINTER.matches || !DESKTOP.matches) {
          row.removeAttribute('aria-controls');
          row.removeAttribute('aria-expanded');
        } else {
          row.setAttribute('aria-controls', `vrp-nav-pane-${row.dataset.vrpTarget}`);
          row.setAttribute('aria-expanded', String(row.dataset.vrpState === 'selected'));
        }
      }
    }

    #hrefOf(row) {
      return row.getAttribute('href') ?? row.dataset.vrpHref;
    }

    // Where focus lands when a row is chosen: on a rotating pane the collection link, which holds still while
    // the prints turn behind it; on a card pane the first card, else the link.
    #firstControl(pane) {
      return pane.querySelector(STAGE) ? pane.querySelector(PREVIEW_LINK) : pane.querySelector(`product-card a[href], ${PREVIEW_LINK}`);
    }

    #openPreview(row, moveFocus, intent) {
      const key = row.dataset.vrpTarget;
      const pane = this.#paneByKey(key);
      if (!pane) return;
      clearTimeout(this.#hoverTimer);
      if (this.hasAttribute('stable-preview-loading')) {
        this.#preparePreview(pane, row, moveFocus, intent);
        return;
      }
      this.#direction = 1;
      if (!this.#previewActive(pane)) {
        if (pane.dataset.vrpPane === '2') {
          this.#l2 = key;
          this.#l3 = null;
        } else {
          this.#l3 = key;
        }
        this.#render();
      }
      this.#fillPreview(pane, row, moveFocus);
    }

    #cancelPreview() {
      clearTimeout(this.#hoverTimer);
      this.#stopRotation();
      this.#previewRequest += 1;
      this.#pendingPreview?.row.removeAttribute('aria-busy');
      this.#pendingPreview = null;
    }

    async #preparePreview(pane, row, moveFocus, intent) {
      if (!this.isConnected || !this.open || this.#leaving) return;
      this.#cancelPreview();
      const request = this.#previewRequest;
      const focusBefore = document.activeElement;
      const body = pane.querySelector(PREVIEW_BODY);
      const current = () => request === this.#previewRequest && this.isConnected && this.open && !this.#leaving;
      this.#pendingPreview = { row, intent };
      row.setAttribute('aria-busy', 'true');
      if (body.dataset.vrpPreviewState !== 'ready') {
        let prints;
        try {
          prints = await this.#loadPreview(pane.dataset.vrpPreview);
        } catch {
          if (!current()) return;
          this.#cancelPreview();
          if (moveFocus && document.activeElement === focusBefore) window.location.assign(this.#hrefOf(row));
          return;
        }
        if (!current()) return;
        this.#insertPreview(pane, prints);
      }
      this.#pendingPreview = null;
      row.removeAttribute('aria-busy');
      this.#direction = 1;
      if (pane.dataset.vrpPane === '2') {
        this.#l2 = row.dataset.vrpTarget;
        this.#l3 = null;
      } else {
        this.#l3 = row.dataset.vrpTarget;
      }
      this.#render();
      if (moveFocus && document.activeElement === focusBefore) {
        this.#focusQuietly(this.#firstControl(pane));
      }
      this.#startRotation(pane);
    }

    #previewActive(pane) {
      if (!pane || pane.hidden) return false;
      return pane.dataset.vrpKey === (pane.dataset.vrpPane === '2' ? this.#l2 : this.#l3);
    }

    // The section rendered on the collection, or on a filtered row's own address, filter and all, so
    // its prints are that filter's. Resolves to the parsed response; a collection with no products has
    // no list in it.
    #loadPreview(preview) {
      const path = preview.includes('/') ? preview.split('#')[0] : `${window.Shopify.routes.root}collections/${encodeURIComponent(preview)}`;
      if (!PREVIEW_CACHE.has(path)) {
        const url = `${path}${path.includes('?') ? '&' : '?'}section_id=${PREVIEW_SECTION}`;
        const request = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`${response.status} ${url}`);
            return response.text();
          })
          .then((html) => new DOMParser().parseFromString(html, 'text/html'))
          .catch((error) => {
            PREVIEW_CACHE.delete(path);
            throw error;
          });
        PREVIEW_CACHE.set(path, request);
      }
      return PREVIEW_CACHE.get(path);
    }

    // Puts the fetched prints into the pane once. A chosen row (click, Enter, Space) then takes focus
    // to the stage or the first card, or to the collection link when there are none; a chosen row
    // whose fetch fails goes to its page instead, so the link stays real.
    async #fillPreview(pane, row, moveFocus) {
      const body = pane.querySelector(PREVIEW_BODY);
      const focusInside = () => {
        if (moveFocus && this.open && !this.#leaving && this.#previewActive(pane)) {
          this.#focusQuietly(this.#firstControl(pane));
        }
      };
      if (body.dataset.vrpPreviewState === 'ready') {
        focusInside();
        this.#startRotation(pane);
        return;
      }
      body.dataset.vrpPreviewState = 'loading';
      body.setAttribute('aria-busy', 'true');
      let prints;
      try {
        prints = await this.#loadPreview(pane.dataset.vrpPreview);
      } catch {
        body.dataset.vrpPreviewState = 'failed';
        body.removeAttribute('aria-busy');
        // The chosen row goes to its page, unless the drawer has closed or another row was chosen since.
        if (moveFocus && this.open && !this.#leaving && this.#previewActive(pane)) window.location.assign(this.#hrefOf(row));
        return;
      }
      // A hover and a click on the same row both wait on one fetch; the second to arrive only focuses.
      if (body.dataset.vrpPreviewState === 'ready') {
        focusInside();
        this.#startRotation(pane);
        return;
      }
      this.#insertPreview(pane, prints);
      body.removeAttribute('aria-busy');
      focusInside();
      this.#startRotation(pane);
    }

    // The cards the header's count allows or, for a rotating preview, the first print on its stage
    // with the rest kept aside until their turn.
    #insertPreview(pane, prints) {
      const body = pane.querySelector(PREVIEW_BODY);
      if (pane.classList.contains('vrp-nav-drawer__pane--rotating')) {
        const stageItems = Array.from(prints.querySelector(PREVIEW_STAGE)?.content.children ?? []);
        const items = this.hasAttribute('data-filtered-preview-all') ? stageItems : stageItems.slice(0, this.#filteredCount());
        if (items.length) {
          const frame = document.createElement('a');
          frame.className = 'vrp-nav-drawer__media-frame';
          frame.dataset.vrpStage = '';
          STAGES.set(frame, { items, index: 0 });
          body.querySelector('.vrp-nav-drawer__media').append(frame);
          this.#showPrint(frame, 0);
        }
      } else {
        const list = prints.querySelector(PREVIEW_LIST);
        if (list) {
          const carousel = document.importNode(list, true);
          const count = pane.dataset.vrpPreviewKind === FILTERED ? this.#filteredCount() : Number(this.dataset.previewCount) || 4;
          Array.from(carousel.children).slice(count).forEach((cell) => cell.remove());
          body.querySelector(PREVIEW_LINK).before(carousel);
        }
      }
      body.dataset.vrpPreviewState = 'ready';
    }

    #filteredCount() {
      return Math.min(8, Math.max(1, Number(this.dataset.filteredPreviewCount) || 6));
    }

    #filteredSeconds() {
      return Math.max(1, Number(this.dataset.filteredPreviewSeconds) || 3);
    }

    // One print on the stage and the next one behind it, so a turn never waits on the network and
    // nothing beyond the next is requested. The print leaving stays until its fade has run, unless
    // it is the next one due (a stage of two).
    #showPrint(frame, index) {
      const stage = STAGES.get(frame);
      const count = stage.items.length;
      const print = stage.items[index];
      stage.index = index;
      frame.href = print.getAttribute('href');
      frame.setAttribute('aria-label', print.getAttribute('aria-label') ?? '');
      const place = (i) => {
        let image = frame.querySelector(`img[data-vrp-print="${i}"]`);
        if (!image) {
          image = document.importNode(stage.items[i].querySelector('img'), true);
          image.dataset.vrpPrint = i;
          image.loading = 'eager';
          frame.append(image);
        }
        return image;
      };
      const current = place(index);
      const next = (index + 1) % count;
      for (const image of frame.querySelectorAll('img')) {
        if (image === current) continue;
        image.classList.remove('is-current');
        if (Number(image.dataset.vrpPrint) !== next) setTimeout(() => image.remove(), STAGE_FADE);
      }
      // A print just put on the stage starts its fade from where the stylesheet left it.
      void current.offsetWidth;
      current.classList.add('is-current');
      if (count > 1 && !REDUCED_MOTION.matches) place(next);
    }

    // The prints of a rotating preview turn only while the pointer or the keyboard focus rests on
    // the row or on the preview, and stop as soon as either leaves; a visitor who asked for less
    // motion, or a tab out of sight, keeps the print showing. Nothing announces a turn. The stage
    // itself is a link, so while it holds keyboard focus its name and destination stay put.
    #startRotation(pane) {
      const frame = pane?.querySelector(STAGE);
      if (!frame || REDUCED_MOTION.matches || document.hidden) return;
      if (frame.matches(':focus') || document.activeElement === frame) {
        if (this.#rotation?.frame === frame) this.#stopRotation();
        return;
      }
      if (this.#rotation?.frame === frame) return;
      this.#stopRotation();
      if (STAGES.get(frame).items.length < 2) return;
      const rows = Array.from(this.querySelectorAll(`${ROW}[data-vrp-target="${pane.dataset.vrpKey}"]`));
      if (!pane.matches(':hover, :focus-within') && !rows.some((row) => row.matches(':hover, :focus'))) return;
      this.#rotation = { frame, pane, rows, timer: null };
      this.#scheduleTurn(this.#rotation);
    }

    #stopRotation() {
      if (!this.#rotation) return;
      clearTimeout(this.#rotation.timer);
      this.#rotation = null;
    }

    #rotationHolds(target) {
      const { pane, rows } = this.#rotation;
      return !!target && (pane.contains(target) || rows.some((row) => row.contains(target)));
    }

    #scheduleTurn(state) {
      state.timer = setTimeout(() => this.#turn(state), this.#filteredSeconds() * 1000);
    }

    // The next print takes the stage once it has arrived, so a slow image delays the turn rather than
    // showing an empty frame.
    #turn(state) {
      if (REDUCED_MOTION.matches) return this.#stopRotation();
      const stage = STAGES.get(state.frame);
      const next = (stage.index + 1) % stage.items.length;
      const image = state.frame.querySelector(`img[data-vrp-print="${next}"]`);
      const ready = image && !image.complete ? image.decode().catch(() => {}) : Promise.resolve();
      ready.then(() => {
        if (this.#rotation !== state) return;
        this.#showPrint(state.frame, next);
        this.#scheduleTurn(state);
      });
    }

    // The first tab's group: the phone's first screen, and desktop's default.
    #home() {
      return this.querySelector('[data-vrp-pane="1"]')?.dataset.vrpFor ?? null;
    }

    // A trigger value with no pane-1 group behind it (the menu icon's "0", or none) means "start at the first tab".
    #groupFor(value) {
      return value && this.querySelector(`[data-vrp-pane="1"][data-vrp-for="${value}"]`) ? value : null;
    }

    #entryPane() {
      return this.#pane1();
    }

    #pane1() {
      return this.querySelector(`[data-vrp-pane="1"][data-vrp-for="${this.#l1}"]`);
    }

    #paneByKey(key) {
      return key ? this.querySelector(`[data-vrp-key="${key}"]`) : null;
    }
  }

  customElements.define('vrp-nav-drawer', VrpNavDrawer);
});
